<script lang="ts">
  import { push } from 'svelte-spa-router';
  import { onMount } from 'svelte';
  import PasswordInput from '../../components/PasswordInput.svelte';
  import Notification from '../../components/Notification.svelte';
  import AuthPageLayout from '../../components/auth/AuthPageLayout.svelte';
  import { api, type ApiResponse } from '../../lib/api/client';
  import { AuthService } from '../../lib/api/auth.service';

  export let params: { uuid?: string } = {};

  let invitation: InvitationDetails | null = null;

  interface InvitationDetails {
    uuid: string;
    invitation_type: string;
    organisation_name: string;
    contact_email: string;
  }

  let loadingInvitation = true;
  let invitationError = '';
  let loading = false;
  let formError = '';

  let email = '';
  let full_name = '';
  let phone = '';
  let password = '';
  let password_confirmation = '';
  let abn = '';

  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  let fieldErrors: Record<string, string[]> = {};

  onMount(async () => {
    const uuid = params.uuid;
    if (!uuid) {
      invitationError = 'Invalid invitation link.';
      loadingInvitation = false;
      return;
    }

    try {
      const response = await api.get<ApiResponse<InvitationDetails>>(`/user-invitation/${uuid}/details`);
      if (response.data?.success && response.data?.data) {
        invitation = response.data.data;
        email = invitation?.contact_email || '';
      } else {
        invitationError = response.data?.message || 'Could not load invitation details.';
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      if (status === 410) {
        invitationError = msg || 'This invitation has expired or was already accepted.';
      } else if (status === 404) {
        invitationError = 'Invitation not found. Please check the link in your email.';
      } else {
        invitationError =
          msg || err?.message || 'Could not load invitation details. Please try again later.';
      }
    } finally {
      loadingInvitation = false;
    }
  });

  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
  }

  function getFieldError(field: string): string {
    return fieldErrors[field]?.[0] || '';
  }

  function clearFieldError(field: string) {
    if (fieldErrors[field]) {
      delete fieldErrors[field];
      fieldErrors = { ...fieldErrors };
    }
  }

  function validateEmail(val: string): string | null {
    if (!val) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) return 'Please enter a valid email address';
    return null;
  }

  function validatePassword(val: string): string | null {
    if (!val) return 'Password is required';
    if (val.length < 8) return 'Password must be at least 8 characters long';
    return null;
  }

  function validatePasswordConfirmation(pwd: string, confirm: string): string | null {
    if (!confirm) return 'Password confirmation is required';
    if (pwd !== confirm) return 'Passwords do not match';
    return null;
  }

  function validateABN(val: string): string | null {
    if (!val) return 'ABN is required';
    const cleanABN = val.replace(/\s/g, '');
    if (cleanABN.length !== 11) return 'ABN must be 11 digits';
    if (!/^\d+$/.test(cleanABN)) return 'ABN must contain only numbers';
    return null;
  }

  function validateRequired(value: string, fieldName: string): string | null {
    if (!value || value.trim() === '') return `${fieldName} is required`;
    return null;
  }

  function validateForm(): boolean {
    const errors: Record<string, string[]> = {};

    const emailErr = validateEmail(email);
    if (emailErr) errors.email = [emailErr];

    const nameErr = validateRequired(full_name, 'Full name');
    if (nameErr) errors.full_name = [nameErr];

    const passwordErr = validatePassword(password);
    if (passwordErr) errors.password = [passwordErr];

    const confirmErr = validatePasswordConfirmation(password, password_confirmation);
    if (confirmErr) errors.password_confirmation = [confirmErr];

    const abnErr = validateABN(abn);
    if (abnErr) errors.abn = [abnErr];

    fieldErrors = errors;
    return Object.keys(errors).length === 0;
  }

  async function handleAcceptInvitation() {
    formError = '';
    fieldErrors = {};

    if (!validateForm()) return;
    if (!invitation) return;

    loading = true;

    try {
      const response = await api.post<ApiResponse<unknown>>(`/user-invitation/${invitation.uuid}/accept`, {
        email,
        full_name,
        phone,
        password,
        password_confirmation,
        abn,
      });

      if (response.data?.success) {
        showNotification('Registration completed successfully! Logging you in...', 'success');

        try {
          const loginResult = await AuthService.login({ email, password });
          if (loginResult.success) return;
        } catch {
          // fall through to login redirect
        }

        setTimeout(() => push('/login'), 2000);
      } else {
        formError = response.data?.message || 'Failed to accept invitation.';
      }
    } catch (err: any) {
      if (err?.response?.data) {
        const apiError = err.response.data;
        if (apiError.data?.errors) {
          fieldErrors = apiError.data.errors;
        } else {
          formError = apiError.message || 'Failed to accept invitation.';
        }
      } else {
        formError = 'An unexpected error occurred. Please try again.';
      }
    } finally {
      loading = false;
    }
  }
</script>

{#if loadingInvitation}
  <AuthPageLayout title="Accept invitation" subtitle="Loading your invitation details..." maxWidth="520px">
    <div class="auth-loading">
      <div class="auth-spinner" aria-hidden="true"></div>
      <p>Please wait a moment</p>
    </div>
  </AuthPageLayout>
{:else if invitationError}
  <AuthPageLayout title="Invitation unavailable" subtitle="This invitation cannot be used.">
    <div class="auth-status-panel">
      <svg
        width="56"
        height="56"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="auth-status-icon error"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
      <p>{invitationError}</p>
      <div class="auth-actions">
        <button type="button" class="auth-btn" on:click={() => push('/login')}>
          Go to sign in
        </button>
      </div>
    </div>
  </AuthPageLayout>
{:else if invitation}
  <AuthPageLayout
    title="Complete registration"
    subtitle="Accept your invitation to join Leave2Give."
    maxWidth="520px"
  >
    <div class="auth-badge" role="status">
      <span class="auth-badge-label">Registering as</span>
      <span class="auth-badge-value">
        {invitation.invitation_type === 'charity' ? 'Charity organisation' : 'Employer'}
      </span>
    </div>

    <div class="auth-org-chip">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
      <span>{invitation.organisation_name}</span>
    </div>

    <form on:submit|preventDefault={handleAcceptInvitation}>
      {#if formError}
        <div class="auth-form-error" role="alert">{formError}</div>
      {/if}

      <section class="auth-form-section" aria-labelledby="invite-details-heading">
        <h2 id="invite-details-heading" class="auth-form-section-title">Your details</h2>
        <div class="auth-form-section-body">
          <div class="auth-form-group">
            <label for="full_name">
              Full name <span class="auth-required" aria-label="required">*</span>
            </label>
            <input
              id="full_name"
              type="text"
              class="auth-input"
              class:error={getFieldError('full_name')}
              bind:value={full_name}
              placeholder="Your full name"
              required
              autocomplete="name"
              aria-invalid={!!getFieldError('full_name')}
              on:input={() => clearFieldError('full_name')}
            />
            {#if getFieldError('full_name')}
              <p class="auth-field-error" role="alert">{getFieldError('full_name')}</p>
            {/if}
          </div>

          <div class="auth-form-group">
            <label for="email">
              Email address <span class="auth-required" aria-label="required">*</span>
            </label>
            <input
              id="email"
              type="email"
              class="auth-input"
              class:error={getFieldError('email')}
              bind:value={email}
              placeholder="your@email.com"
              required
              autocomplete="email"
              aria-invalid={!!getFieldError('email')}
              on:input={() => clearFieldError('email')}
            />
            {#if getFieldError('email')}
              <p class="auth-field-error" role="alert">{getFieldError('email')}</p>
            {/if}
          </div>

          <div class="auth-form-group">
            <label for="phone">Phone number</label>
            <input
              id="phone"
              type="tel"
              class="auth-input"
              bind:value={phone}
              placeholder="04XX XXX XXX"
              autocomplete="tel"
            />
          </div>

          <div class="auth-form-group">
            <label for="abn">
              ABN <span class="auth-required" aria-label="required">*</span>
            </label>
            <input
              id="abn"
              type="text"
              class="auth-input"
              class:error={getFieldError('abn')}
              bind:value={abn}
              placeholder="11-digit ABN"
              maxlength="14"
              required
              aria-invalid={!!getFieldError('abn')}
              on:input={() => clearFieldError('abn')}
            />
            {#if getFieldError('abn')}
              <p class="auth-field-error" role="alert">{getFieldError('abn')}</p>
            {/if}
          </div>
        </div>
      </section>

      <section class="auth-form-section" aria-labelledby="invite-security-heading">
        <h2 id="invite-security-heading" class="auth-form-section-title">Account security</h2>
        <div class="auth-form-section-body">
          <div class="auth-password-row">
            <div class="auth-form-group">
              <label for="password">
                Create password <span class="auth-required" aria-label="required">*</span>
              </label>
              <PasswordInput
                id="password"
                bind:value={password}
                placeholder="At least 8 characters"
                autocomplete="new-password"
                ariaInvalid={!!getFieldError('password')}
                className={getFieldError('password') ? 'error' : ''}
                on:input={() => clearFieldError('password')}
              />
              {#if getFieldError('password')}
                <p class="auth-field-error" role="alert">{getFieldError('password')}</p>
              {/if}
            </div>

            <div class="auth-form-group">
              <label for="password_confirmation">
                Confirm password <span class="auth-required" aria-label="required">*</span>
              </label>
              <PasswordInput
                id="password_confirmation"
                bind:value={password_confirmation}
                placeholder="Re-enter password"
                autocomplete="new-password"
                ariaInvalid={!!getFieldError('password_confirmation')}
                className={getFieldError('password_confirmation') ? 'error' : ''}
                on:input={() => clearFieldError('password_confirmation')}
              />
              {#if getFieldError('password_confirmation')}
                <p class="auth-field-error" role="alert">{getFieldError('password_confirmation')}</p>
              {/if}
            </div>
          </div>
          <p class="auth-field-hint">Password must be at least 8 characters long</p>
        </div>
      </section>

      <div class="auth-actions">
        <button type="submit" class="auth-btn" disabled={loading}>
          {loading ? 'Completing registration...' : 'Complete registration'}
        </button>
      </div>
    </form>

    <svelte:fragment slot="footer">
      Already have an account?
      <a href="/login" on:click|preventDefault={() => push('/login')}>Sign in</a>
    </svelte:fragment>
  </AuthPageLayout>
{/if}

<Notification
  bind:show={notificationShow}
  type={notificationType}
  message={notificationMessage}
  duration={2000}
  onDismiss={() => { notificationShow = false; }}
/>
